import { ApiService } from '@/services/api/ApiService';

export interface TranslateOptions {
  text: string;
  targetLanguage: string;
  sourceLanguage?: string;
}

export interface EnhancePromptOptions {
  currentPrompt: string;
  agentName?: string;
  agentDescription?: string;
  userInstructions?: string;
}

export interface SuggestPronunciationsOptions {
  pronunciations: { word: string; pronunciation: string }[];
  accent: string;
}

export interface ImportPronunciationsFromFileOptions {
  fileContent: string;
  accent?: string;
}

export interface SummarizeDocumentOptions {
  text: string;
  documentName?: string;
}

export interface DocumentSummaryResult {
  summary: string;
  answers: string[];
  topics: string[];
}

export interface ToolCallForExplain {
  toolName: string;
  request: unknown;
  response: unknown;
  status: string;
  error: string | null;
}

export interface ExplainToolErrorsOptions {
  /** Every tool call made while producing this assistant message, in order — not just the failed one(s). */
  toolCalls: ToolCallForExplain[];
  /** The assistant's final reply text, for context on how/whether it recovered from the failure. */
  assistantReply?: string;
}

export class AIChatService {
  static async translate(options: TranslateOptions): Promise<string> {
    try {
      const response = await ApiService.callEdge<{ response: string }>("ai-chat", {
        action: "translate",
        messages: [],
        data: {
          text: options.text,
          targetLanguage: options.targetLanguage,
          sourceLanguage: options.sourceLanguage,
        },
      });

      if (!response?.response) {
        throw new Error("No translation returned");
      }

      return response.response;
    } catch (error) {
      console.error("Error in AIChatService translate:", error);
      throw error;
    }
  }

  static async enhancePrompt(options: EnhancePromptOptions): Promise<string> {
    try {
      const response = await ApiService.callEdge<{ response: string }>("ai-chat", {
        action: "enhance-prompt",
        messages: [],
        data: {
          currentPrompt: options.currentPrompt,
          agentName: options.agentName,
          agentDescription: options.agentDescription,
          userInstructions: options.userInstructions,
        },
      });

      if (!response?.response) {
        throw new Error("No enhanced prompt returned");
      }

      return response.response;
    } catch (error) {
      console.error("Error in AIChatService enhancePrompt:", error);
      throw error;
    }
  }

  static async importPronunciationsFromFile(options: ImportPronunciationsFromFileOptions): Promise<{ word: string; pronunciation: string }[]> {
    try {
      const response = await ApiService.callEdge<{ response: string }>("ai-chat", {
        action: "import-pronunciations-from-file",
        messages: [],
        data: {
          fileContent: options.fileContent,
          accent: options.accent,
        },
      });

      if (!response?.response) {
        throw new Error("No pairs returned");
      }

      return JSON.parse(response.response);
    } catch (error) {
      console.error("Error in AIChatService importPronunciationsFromFile:", error);
      throw error;
    }
  }

  static async suggestPronunciations(options: SuggestPronunciationsOptions): Promise<{ word: string; pronunciation: string }[]> {
    try {
      const response = await ApiService.callEdge<{ response: string }>("ai-chat", {
        action: "suggest-pronunciations",
        messages: [],
        data: {
          pronunciations: options.pronunciations,
          accent: options.accent,
        },
      });

      if (!response?.response) {
        throw new Error("No suggestions returned");
      }

      return JSON.parse(response.response);
    } catch (error) {
      console.error("Error in AIChatService suggestPronunciations:", error);
      throw error;
    }
  }

  /**
   * Summarizes a knowledge-base source document into a plain-language summary,
   * the questions it can answer, and a handful of topic tags.
   *
   * Uses the "ai-chat" edge function's freeform chat path (no `action`), the same
   * way `translate`/`suggestPronunciations` reach it, just without a dedicated
   * server-side handler — the JSON contract is enforced entirely by the prompt and
   * parsed here, mirroring `suggestPronunciations`'s client-side JSON.parse.
   */
  static async summarizeDocument(options: SummarizeDocumentOptions): Promise<DocumentSummaryResult> {
    try {
      const response = await ApiService.callEdge<{ response: string }>("ai-chat", {
        messages: [
          {
            role: "system",
            content:
              "You are helping a support team understand a source document that has been added to their knowledge base. " +
              "Read the document text and return ONLY a valid JSON object, no markdown fences, no explanation, matching this shape exactly: " +
              '{"summary": string, "answers": string[], "topics": string[]}. ' +
              '"summary" is one plain-language sentence describing what this document covers. ' +
              '"answers" is 3 to 6 short questions this document can answer, phrased the way a real user would ask them. ' +
              '"topics" is 2 to 4 short topic tags (1-3 words each).',
          },
          {
            role: "user",
            content: options.documentName
              ? `Document name: ${options.documentName}\n\nDocument text:\n${options.text}`
              : `Document text:\n${options.text}`,
          },
        ],
        config: { temperature: 0.3 },
      });

      if (!response?.response) {
        throw new Error("No summary returned");
      }

      const cleaned = response.response
        .replace(/^```(?:json)?\s*/i, "")
        .replace(/\s*```\s*$/, "")
        .trim();

      const parsed = JSON.parse(cleaned);

      if (
        typeof parsed?.summary !== "string" ||
        !Array.isArray(parsed?.answers) ||
        !Array.isArray(parsed?.topics)
      ) {
        throw new Error("Summary response was not in the expected shape");
      }

      const asStringList = (list: unknown[]): string[] =>
        list.filter((item): item is string => typeof item === "string" && item.trim().length > 0).map((item) =>
          item.trim(),
        );

      return {
        summary: parsed.summary.trim(),
        answers: asStringList(parsed.answers),
        topics: asStringList(parsed.topics),
      };
    } catch (error) {
      console.error("Error in AIChatService summarizeDocument:", error);
      throw error;
    }
  }

  /**
   * Explains why one or more tool calls failed within an assistant turn, for an admin
   * reading brain chat logs. Takes every tool call made in that turn (not just the failed
   * ones) since a failure often only makes sense next to what was tried before/after it —
   * e.g. a retry with different params, or a later call that worked around it.
   * In-time only — never persisted — so this is deliberately a freeform "ai-chat" call
   * rather than a dedicated handler, mirroring summarizeDocument.
   */
  static async explainToolErrors(options: ExplainToolErrorsOptions): Promise<string> {
    try {
      const callsBlock = options.toolCalls
        .map((call, idx) =>
          [
            `Call ${idx + 1}: ${call.toolName} (${call.status})`,
            `  Request: ${JSON.stringify(call.request)}`,
            `  Response: ${JSON.stringify(call.response)}`,
            call.status === "error" ? `  Error: ${call.error ?? "(none provided)"}` : null,
          ]
            .filter(Boolean)
            .join("\n"),
        )
        .join("\n\n");

      const response = await ApiService.callEdge<{ response: string }>("ai-chat", {
        messages: [
          {
            role: "system",
            content:
              "You are helping a support-team admin understand why one or more tool calls failed during a single " +
              "AI assistant turn. You are given every tool call made in that turn, in order, not just the failed " +
              "ones — use the full sequence for context (what was tried before/after, whether a later call worked " +
              "around the failure) rather than judging the failed call in isolation. " +
              "Reply with a 1-2 sentence plain-language summary of what went wrong and, if obvious, why. " +
              "No markdown, no preamble.",
          },
          {
            role: "user",
            content: [
              callsBlock,
              options.assistantReply ? `\nAssistant's final reply to the user: ${options.assistantReply}` : null,
            ]
              .filter(Boolean)
              .join("\n"),
          },
        ],
        config: { temperature: 0.2 },
      });

      if (!response?.response) {
        throw new Error("No explanation returned");
      }

      return response.response.trim();
    } catch (error) {
      console.error("Error in AIChatService explainToolErrors:", error);
      throw error;
    }
  }
}
